import { create } from 'zustand';
import {
  OAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';

import { auth } from '../firebaseConfig';
import { apiBackendLogin } from '../services/api';

const STORAGE_KEYS = {
  user: 'sr_user',
  userData: 'sr_user_data',
  parentAccess: 'sr_parent_access',
};

function readJson(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function persistNonSensitiveAuth({ backendData }) {
  const appUser = backendData?.user || null;
  const parentAccess = backendData?.parent_access || null;

  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(appUser));
  localStorage.setItem(STORAGE_KEYS.userData, JSON.stringify(backendData || null));

  if (parentAccess) {
    localStorage.setItem(STORAGE_KEYS.parentAccess, JSON.stringify(parentAccess));
  } else {
    localStorage.removeItem(STORAGE_KEYS.parentAccess);
  }

  return {
    user: appUser,
    userData: backendData || null,
    parentAccess,
    isAuthenticated: true,
  };
}

async function completeBackendLogin(firebaseUser) {
  const firebaseToken = await firebaseUser.getIdToken(true);
  // POST /api/v1/auth/login
  // Authorization: Bearer <firebaseToken>
  const backendData = await apiBackendLogin(firebaseToken);

  return persistNonSensitiveAuth({ backendData });
}

function clearStoredAuth() {
  localStorage.removeItem(STORAGE_KEYS.user);
  localStorage.removeItem(STORAGE_KEYS.userData);
  localStorage.removeItem(STORAGE_KEYS.parentAccess);
}

function getFriendlyError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return err?.message || fallback;
}

export const useAuthStore = create((set) => ({
  user: readJson(STORAGE_KEYS.user),
  userData: readJson(STORAGE_KEYS.userData),
  parentAccess: readJson(STORAGE_KEYS.parentAccess),

  // Do not store token in Zustand/localStorage.
  isAuthenticated: !!auth.currentUser || !!readJson(STORAGE_KEYS.user),
  authReady: false,

  loading: false,
  error: null,

  initAuthListener: () => {
      return onAuthStateChanged(auth, async (firebaseUser) => {
        if (!firebaseUser) {
          clearStoredAuth();

          set({
            user: null,
            userData: null,
            parentAccess: null,
            isAuthenticated: false,
            authReady: true,
            loading: false,
          });

          return;
        }

        try {
          set({
            loading: true,
            authReady: false,
          });

          const authState = await completeBackendLogin(firebaseUser);

          set({
            ...authState,
            isAuthenticated: true,
            authReady: true,
            loading: false,
            error: null,
          });
        } catch (err) {
          console.error(err);

          clearStoredAuth();

          set({
            user: null,
            userData: null,
            parentAccess: null,
            isAuthenticated: false,
            authReady: true,
            loading: false,
            error: err?.message || 'Could not complete login.',
          });
        }
      });
    },

  loginWithProvider: async (providerName) => {
    set({ loading: true, error: null });

    try {
      let provider;

      if (providerName === 'google') {
        provider = new GoogleAuthProvider();
      } else if (providerName === 'apple') {
        provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
      } else {
        throw new Error('Unsupported provider');
      }

      const result = await signInWithPopup(auth, provider);
      const authState = await completeBackendLogin(result.user);

      set({
        ...authState,
        loading: false,
        error: null,
      });

      return authState.userData;
    } catch (err) {
      console.error(err);
      set({
        error: getFriendlyError(err, 'Sign in failed. Please try again.'),
        loading: false,
      });
      return null;
    }
  },

  login: async (email, password) => {
    set({ loading: true, error: null });

    try {
      const result = await signInWithEmailAndPassword(
        auth,
        String(email || '').trim(),
        password
      );

      const authState = await completeBackendLogin(result.user);

      set({
        ...authState,
        loading: false,
        error: null,
      });

      return authState.userData;
    } catch (err) {
      console.error(err);
      set({
        error: getFriendlyError(err, 'Sign in failed. Please try again.'),
        loading: false,
      });
      return null;
    }
  },

  signup: async (name, email, password) => {
    set({ loading: true, error: null });

    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        String(email || '').trim(),
        password
      );

      if (name?.trim()) {
        await updateProfile(result.user, {
          displayName: name.trim(),
        });
      }

      const authState = await completeBackendLogin(result.user);

      set({
        ...authState,
        loading: false,
        error: null,
      });

      return authState.userData;
    } catch (err) {
      console.error(err);
      set({
        error: getFriendlyError(err, 'Could not create account. Please try again.'),
        loading: false,
      });
      return null;
    }
  },

  logout: async () => {
    try {
      await signOut(auth);
    } catch {
      // Still clear local app state.
    }

    clearStoredAuth();

    set({
      user: null,
      userData: null,
      parentAccess: null,
      isAuthenticated: false,
      loading: false,
      error: null,
    });
  },

  clearError: () => set({ error: null }),
}));